import {
  FORECAST_TRUMPING_FOLDER_ID,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FORECAST_SOURCE
} from "constants/settings.constants";
import { ForecastMode, ForecastModeT } from "store/features/app/appSlice";

type ForecastSelectionT = {
  forecastMode: Maybe<ForecastModeT>;
  useMcDanielForecasts: boolean;
  useNoviForecasts: boolean;
  useWorkingForecasts: boolean;
};

/**
 * Which forecast source the Forecast Source dropdowns should show, given what is ticked in the
 * project Forecasts panel.
 *
 * The rule:
 *  - EVA Research + Novi Labs, in Trump mode  → Forecast Trumping
 *  - exactly one of the two                   → that one
 *  - anything else                            → null, meaning "no opinion"
 *
 * Compare mode is excluded from the trumping case because the panel is then showing two
 * forecasts side by side rather than layering them.
 *
 * The rule is defined over EVA Research and Novi only, so Working Forecasts always yields null
 * rather than being dropped from a mixed selection: returning EVA Research for an
 * EVA-plus-Working panel would name one ticked source and silently ignore the other. Null lets
 * the caller fall back to the saved user default instead of asserting something half-true.
 *
 * `useNoviForecasts` must already account for entitlement — the panel booleans can carry a
 * stale Novi selection (a restored View, a saved default from when the org had Novi), and
 * trusting it here would put Novi in the request for an org that is not entitled to it.
 */
export function getForecastSourceFromSelection({
  forecastMode,
  useMcDanielForecasts,
  useNoviForecasts,
  useWorkingForecasts
}: ForecastSelectionT): Nullable<string> {
  if (useWorkingForecasts) {
    return null;
  }

  if (useMcDanielForecasts && useNoviForecasts) {
    return forecastMode === ForecastMode.Trump ? FORECAST_TRUMPING_FOLDER_ID : null;
  }

  if (useNoviForecasts) {
    return NOVI_FORECAST_SOURCE;
  }

  if (useMcDanielForecasts) {
    return MCDANIEL_SEARCH_FOLDER_NAME;
  }

  return null;
}
